// CalendarPopupEvent.tsx
import React, {
  CSSProperties,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Checkbox, Popup } from "semantic-ui-react";

import moment from "moment";

import ModAppHandleMethod from "./ModAppHandleMethod";
import ModEditSchedule, { ModEditScheduleProps } from "./ModEditSchedule";
import ModScheduleAppointment, { ModScheduleAppointmentProps } from "./ModScheduleAppointment";
import ModScheduleInfo, { ModScheduleInfoProps } from "./ModScheduleInfo";
import ResizableEventContainer, { ResizeParamsType } from "./ResizableEventContainer";

import {
  aggregateSlotInfo,
  AppointmentHandleMethodType,
  filterMatchingProviders,
  findMatchingProvider,
  generateGroupedDateStrings,
  getFormattedEventItems,
  GroupedScheduleEventType,
} from "./sequence/ScheduleSetting";

export type CalendarPopupEventProps = {
  divisionId: number;
  event: GroupedScheduleEventType;
  isMultipleWeeks: boolean;
  isProvider?: boolean;
  providerIds: number[] | null;
  selectedEventIds: number[];
  specialtyIds: number[] | null;
  onCheckbox: (ids: number[]) => void;
  onClose: () => void;
  onDelete: ModScheduleInfoProps["onDelete"];
  onEdit: ModScheduleInfoProps["onEdit"];
  onPublish: ModScheduleInfoProps["onPublish"];
  onResizingChange?: (isResizing: boolean) => void;
  onSave: ModScheduleInfoProps["onSave"];
  onSelectChat: ModScheduleAppointmentProps["onSelectChat"];
  onSelectProvider: ModScheduleAppointmentProps["onSelectProvider"];
  onUpdateDSB: ModEditScheduleProps["onUpdateDSB"];
};

type Styles = Record<"icon" | "providerAvatar", CSSProperties>;

const IMAGES = {
  calendar: "/static/images/schedule/calendar-grey-icon.png",
  division: "/static/images/schedule/division-orange-icon.png",
  edit: "/static/images/schedule/edit-schedule-icon.png",
  provider: "/static/images/schedule/provider-grey-icon.png",
  publishedActive: "/static/images/schedule/published-schedule-active-icon.png",
};

const styles: Styles = {
  icon: { height: "24px", marginTop: "-0.2rem", width: "auto" },
  providerAvatar: {
    alignItems: "center",
    borderRadius: "50%",
    display: "flex",
    fontSize: "13px",
    height: "25px",
    justifyContent: "center",
    width: "25px",
  },
};

const POPUP_OFFSET: [number, number] = [25, 0];

const CalendarPopupEvent = (props: CalendarPopupEventProps) => {
  const [openId, setOpenId] = useState<number | null>(null);
  const [isOpened, setIsOpened] = useState(false);
  const [openModProvider, setOpenModProvider] = useState<boolean>(false);
  const [openModMethod, setOpenModMethod] = useState<boolean>(false); // 👈 เพิ่ม state
  const [openModEdit, setOpenModEdit] = useState<boolean>(false); // 👈 เพิ่ม state
  const [isEditing, setIsEditing] = useState(false);
  const [isMethodLoading, setIsMethodLoading] = useState(false); // 👈 เพิ่ม state
  const [eventTime, setEventTime] = useState({
    end: props.event.end,
    start: props.event.start,
  });
  const [isResizingNow, setIsResizingNow] = useState(false);
  const popupRef = useRef<any>(null);

  useEffect(() => {
    if (props.event.is_draft) {
      setOpenId(props.event.id);

      setTimeout(() => {
        setIsOpened(true);
      }, 250);
    }
  }, [props.event.is_draft]);

  useEffect(() => {
    setEventTime({
      end: props.event.end,
      start: props.event.start,
    });
  }, [props.event.end, props.event.start]);

  const providerCount = useMemo(() => {
    const uniqueProviderIds = new Set(
      props.event.items.flatMap((item) =>
        item.providers.map((provider) => provider.doctor_provider_id)
      )
    );

    return uniqueProviderIds.size;
  }, [props.event]);

  const foundProvider = useMemo(
    () => findMatchingProvider(props.event.items, props.providerIds, props.specialtyIds),
    [props.event, props.providerIds, props.specialtyIds]
  );

  const filteredProvider = useMemo(
    () => filterMatchingProviders(props.event.items, props.providerIds, props.specialtyIds),
    [props.event, props.providerIds, props.specialtyIds]
  );

  const aggregatedSlotInfo = useMemo(() => aggregateSlotInfo(filteredProvider), [filteredProvider]);

  const bgColor = useMemo(() => {
    if (foundProvider) {
      return foundProvider.dsb.profile.color.dark;
    } else if (props.event.is_closed) {
      return "rgba(191, 191, 191, 1)";
    }

    return "rgba(154, 195, 255, 1)";
  }, [foundProvider, props.event]);

  const shouldShowCheckbox = useMemo(() => {
    const canShow = !props.event.is_published || (props.isProvider && !foundProvider);

    // เช็คว่า items ทั้งหมดเป็นวันในอดีตหรือไม่
    const allItemsArePast = props.event.items.every((item) => {
      const itemDate = moment(item.start_datetime);

      return itemDate.isBefore(moment());
    });

    return !props.event.is_draft && !props.event.is_closed && canShow && !allItemsArePast;
  }, [
    foundProvider,
    props.event.is_closed,
    props.event.is_draft,
    props.event.is_published,
    props.event.items,
    props.isProvider,
  ]);

  const formattedEventItems = useMemo(() => {
    if (openId === props.event.id && props.event.is_draft) {
      return getFormattedEventItems(props.event.items, eventTime);
    }

    return props.event.items;
  }, [eventTime, openId, props.event]);

  const handleTimeChange = useCallback(
    (startTime: string, endTime: string) => {
      if (isResizingNow) {
        return;
      }

      const date = moment(props.event.start).format("YYYY-MM-DD");
      const updateStart = moment(`${date} ${startTime}`, "YYYY-MM-DD HH:mm");
      const updateEnd = moment(`${date} ${endTime}`, "YYYY-MM-DD HH:mm");

      setEventTime((prev) => {
        const prevStart = moment(prev.start);
        const prevEnd = moment(prev.end);

        if (prevStart.isSame(updateStart) && prevEnd.isSame(updateEnd)) {
          return prev;
        }

        return {
          end: updateEnd.toDate(),
          start: updateStart.toDate(),
        };
      });
    },
    [isResizingNow, props.event.start]
  );

  const handleResizing = useCallback(
    ({ endTime, startTime }: ResizeParamsType) => {
      setIsResizingNow(true);

      if (props.onResizingChange) {
        props.onResizingChange(true);
      }

      setEventTime({
        end: endTime.toDate(),
        start: startTime.toDate(),
      });

      if (popupRef.current) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        popupRef.current.forceUpdate?.();
      }
    },
    [props.onResizingChange]
  );

  const handleResizeEnd = useCallback(
    ({ endTime, startTime }: ResizeParamsType) => {
      setEventTime({
        end: endTime.toDate(),
        start: startTime.toDate(),
      });

      setTimeout(() => {
        setIsResizingNow(false);

        if (props.onResizingChange) {
          props.onResizingChange(false);
        }
      }, 50);
    },
    [props.event.id, props.onResizingChange]
  );

  const handleOpenMethodModal = useCallback(() => {
    setOpenModMethod(true);

    handleClose(false);
  }, []);

  const handleSaveMethod = useCallback(
    async (selectedMethod: AppointmentHandleMethodType) => {
      const eventIds = props.event.items.map((item) => item.id);

      setIsMethodLoading(true);

      const data = {
        date: moment(eventTime.start),
        end_time: moment(eventTime.end).format("HH:mm"),
        start_time: moment(eventTime.start).format("HH:mm"),
      };

      await props.onEdit({ ...data, ids: eventIds, method: selectedMethod });

      setIsMethodLoading(false);
      setOpenModMethod(false);
    },
    [eventTime]
  );

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    const items = props.event.items;

    props.onCheckbox(items.map((item) => item.id));
  };

  const handlePopupClose = () => {
    if (isOpened) {
      props.onClose();

      handleClose();
    }
  };

  const handleOpen = () => {
    if (!props.event.is_draft && !props.isProvider && !props.event.is_closed) {
      setOpenId(props.event.id);
      setIsOpened(true);
    }
  };

  const handleClose = (isReset = true) => {
    setOpenId(null);
    setIsOpened(false);

    if (!props.event.is_draft && isReset) {
      setEventTime({
        end: props.event.end,
        start: props.event.start,
      });
    }
  };

  const handleOpenModProvider = (e: SyntheticEvent) => {
    if (!props.isProvider) {
      return;
    }

    e.stopPropagation();

    setOpenModProvider(true);
  };

  return (
    <>
      <Popup
        ref={popupRef}
        style={{ border: "1px solid rgba(243, 130, 166, 1)", borderRadius: "6px" }}
        offset={POPUP_OFFSET}
        open={openId === props.event.id}
        position="bottom left"
        on="click"
        onClose={handlePopupClose}
        onOpen={handleOpen}
        content={
          openId === props.event.id && (
            <ModScheduleInfo
              data={props.event}
              // disabledDate={props.isMultipleWeeks || !props.event.is_draft}
              eventTime={eventTime}
              toolbars={!props.event.is_draft}
              open
              onClose={handleClose}
              onDelete={props.onDelete}
              onEdit={props.onEdit}
              onEditingChange={setIsEditing}
              onOpenMethodModal={handleOpenMethodModal} // 👈 ส่ง prop
              onPublish={props.onPublish}
              onSave={props.onSave}
              onTimeChange={handleTimeChange}
            />
          )
        }
        trigger={
          <ResizableEventContainer
            disabled={!isEditing || openId !== props.event.id}
            endTime={eventTime.end}
            hourHeight={73.5}
            maxTime="23:59"
            minDurationMinutes={5}
            minTime="00:00"
            minutesPerStep={5}
            startTime={eventTime.start}
            onClick={handleOpen}
            onResizeEnd={handleResizeEnd}
            onResizing={handleResizing}
            tooltip={
              foundProvider && (
                <>
                  <div style={{ fontWeight: "bold" }}>{foundProvider.dsb.doctor_provider_name}</div>
                  <div>[{foundProvider.dsb.specialty}]</div>
                  <div style={{ fontWeight: "bold", marginTop: "0.25rem" }}>ตารางให้บริการ:</div>
                  {foundProvider.dsb.division_id !== props.divisionId && (
                    <div style={{ color: "rgba(215, 123, 36, 1)", margin: "0.25rem 0" }}>
                      {foundProvider.dsb.division_name}
                    </div>
                  )}
                  <div>
                    {props.event.full_date} [{foundProvider.dsb.doctor_start_time} -{" "}
                    {foundProvider.dsb.doctor_end_time}]
                  </div>
                  <div style={{ marginTop: "0.25rem" }}>
                    จำนวนนัดหมาย: {aggregatedSlotInfo.statusText}
                  </div>
                </>
              )
            }
          >
            <div
              style={{
                alignItems: "center",
                display: "flex",
                gap: "0.75rem",
                marginBottom: "3px",
              }}
            >
              {props.event.is_published && !foundProvider && (
                <div>
                  <img
                    style={styles.icon}
                    alt="publishedActive.icon"
                    src={IMAGES.publishedActive}
                  />
                </div>
              )}
              {shouldShowCheckbox && (
                <Checkbox
                  className="primary"
                  style={{ transform: "scale(1.15)" }}
                  onClick={handleCheckboxClick}
                  checked={props.event.items.some((item) =>
                    props.selectedEventIds.includes(item.id)
                  )}
                />
              )}

              {foundProvider && (
                <>
                  <div
                    style={{
                      ...styles.providerAvatar,
                      backgroundColor:
                        foundProvider.dsb.doctor_dsb_status === 2
                          ? "rgb(204, 204, 204)"
                          : foundProvider.dsb.profile.color.dark,
                    }}
                  >
                    {foundProvider.dsb.profile.abbr}
                  </div>
                  {!props.isMultipleWeeks && (
                    <img
                      style={{ height: "20px" }}
                      alt="edit.icon"
                      src={IMAGES.edit}
                      onClick={() => {
                        setOpenModEdit(true);
                      }}
                    />
                  )}
                </>
              )}
            </div>

            <div style={{ lineHeight: "18px", marginBottom: "0.15rem" }}>
              {props.event.is_draft
                ? generateGroupedDateStrings(formattedEventItems)
                    .full_date_first_month_only_without_past
                : props.event.full_date_first_month_only}
            </div>
            <div style={{ lineHeight: "18px" }}>{props.event.title}</div>
            {props.event.has_provider && (
              <div
                style={{
                  backgroundColor: bgColor,
                  display: "flex",
                  margin: "0.25rem -8px 0",
                  padding: "0.35rem 0.5rem 0.25rem",
                }}
                onClick={handleOpenModProvider}
              >
                {!foundProvider && (
                  <>
                    <img
                      style={{ marginRight: "0.5rem", width: "14px" }}
                      alt="provider.icon"
                      src={IMAGES.provider}
                    />
                    <div style={{ marginRight: "0.5rem" }}>{providerCount}</div>
                  </>
                )}
                {(props.event.has_appointment || !!foundProvider) && (
                  <>
                    <img
                      style={{
                        height: "14px",
                        marginRight: "0.5rem",
                        marginTop: "-1px",
                        width: "14px",
                      }}
                      alt="calendar.icon"
                      src={IMAGES.calendar}
                    />
                    {aggregatedSlotInfo.providers.length > 0 && (
                      <label style={{ marginLeft: "0.25rem" }}>
                        {aggregatedSlotInfo.statusText}
                      </label>
                    )}
                  </>
                )}
                {props.event.is_closed && "Closed"}
              </div>
            )}
            <div>
              {foundProvider && foundProvider.dsb.division_id !== props.divisionId && (
                <div style={{ display: "flex", marginTop: "0.75rem" }}>
                  <img
                    style={{
                      height: "max-content",
                      marginLeft: "-2px",
                      marginRight: "0.25rem",
                      width: "20px",
                    }}
                    alt="division.icon"
                    src={IMAGES.division}
                  />
                  <label style={{ marginTop: "2px" }}>{foundProvider.dsb.division_name}</label>
                </div>
              )}
            </div>
          </ResizableEventContainer>
        }
      />
      <ModScheduleAppointment
        divisionId={props.divisionId}
        event={props.event}
        open={openModProvider}
        providers={filteredProvider}
        onClose={() => {
          setOpenModProvider(false);
        }}
        onSelectChat={props.onSelectChat}
        onSelectProvider={props.onSelectProvider}
      />
      <ModAppHandleMethod
        loading={isMethodLoading}
        open={openModMethod}
        onClose={() => {
          setOpenModMethod(false);
        }}
        onSave={handleSaveMethod}
      />

      {foundProvider && (
        <ModEditSchedule
          dsb={foundProvider.dsb}
          event={props.event}
          open={openModEdit}
          onClose={() => {
            setOpenModEdit(false);
          }}
          onUpdateDSB={props.onUpdateDSB}
        />
      )}
    </>
  );
};

CalendarPopupEvent.displayName = "CalendarPopupEvent";

export default React.memo(CalendarPopupEvent);
